import { NgZone, ChangeDetectorRef } from '@angular/core';

export class Geocoder 
{
    private _changeDetector: ChangeDetectorRef;
    private _zone: NgZone;
    private _innerResults: Array<google.maps.GeocoderResult> = [];
    private _results: Array<GeocoderResult> = [];
    private _innerStatus: google.maps.GeocoderStatus;
    private _status: GeocoderStatus = GeocoderStatus.NoResults;

    public get Results(): Array<GeocoderResult> { return this._results; }
    public get Status(): GeocoderStatus { return this._status; }

    constructor(changeDetector: ChangeDetectorRef, zone: NgZone) 
    {
        this._changeDetector = changeDetector;
        this._zone = zone;
        var i = 1;
    }

    public Geocode(address: string, onCompleted: (geocoder: Geocoder) => void): void
    {
        this._results = [];
        this._status = GeocoderStatus.InProgress;
        this._zone.runOutsideAngular(() =>
        {
            var request: google.maps.GeocodingRequest = <any>{};
            request.address = address;
            var geocoder: google.maps.Geocoder = new google.maps.Geocoder();
            geocoder.geocode(request, (r, s) => this.OnGeocodeCompleted(r, s, onCompleted));
        });
    }

    private OnGeocodeCompleted(results: Array<google.maps.GeocoderResult>, status: google.maps.GeocoderStatus, onCompleted: (geocoder: Geocoder) => void): void
    {
        this._innerResults = results;
        this._results = this.ConvertResults(results);

        this._innerStatus = status;
        switch(status)
        {
            case (google.maps.GeocoderStatus.ERROR):
            case (google.maps.GeocoderStatus.UNKNOWN_ERROR):
                this._status = GeocoderStatus.Error;
                break;
            case (google.maps.GeocoderStatus.INVALID_REQUEST):
                this._status = GeocoderStatus.InvalidRequest;
                break;
            case (google.maps.GeocoderStatus.OVER_QUERY_LIMIT):
                this._status = GeocoderStatus.LicensingIssue;
                break;
            case (google.maps.GeocoderStatus.REQUEST_DENIED):
                this._status = GeocoderStatus.RequestDenied;
                break;
            case (google.maps.GeocoderStatus.OK):
                this._status = GeocoderStatus.Success;
                break;
            default:
                this._status = GeocoderStatus.NoResults;
        }

        this._zone.run(() => {
            onCompleted(this);
        });
    }

    private ConvertResults(innerResults: google.maps.GeocoderResult[]): GeocoderResult[]
    {
        var results: GeocoderResult[] = [];
        
        if (innerResults && innerResults.length > 0)
        {
            for(var index: number = 0; index < innerResults.length; index++)
            {
                var innerResult: google.maps.GeocoderResult = innerResults[index];
                var result: GeocoderResult = new GeocoderResult();
                result.Address = innerResult.formatted_address;

                if (innerResult.types)
                    result.Types = innerResult.types.slice();

                if (innerResult.postcode_localities)
                    result.Localities = innerResult.postcode_localities.slice();

                result.Id = innerResult.place_id;
                result.IsPartial = innerResult.partial_match;

                result.Geometry = new Geometry();
                if (innerResult.geometry)
                {
                    if (innerResult.geometry.bounds)
                    {
                        var bounds: google.maps.LatLngBoundsLiteral = innerResult.geometry.bounds.toJSON();
                        result.Geometry.Bounds = new Bounds(bounds.east, bounds.north, bounds.south, bounds.west);
                    }

                    if (innerResult.geometry.viewport)
                    {
                        var viewPort: google.maps.LatLngBoundsLiteral = innerResult.geometry.viewport.toJSON();
                        result.Geometry.Viewport = new Bounds(viewPort.east, viewPort.north, viewPort.south, viewPort.west);
                    }

                    if (innerResult.geometry.location)
                    {
                        result.Geometry.Coordinates = new Coordinates(innerResult.geometry.location.lat(), innerResult.geometry.location.lng(), this.ConvertLocationType(innerResult.geometry.location_type));
                    }
                }

                result.Parts = [];
                
                if (innerResult.address_components && innerResult.address_components.length > 0)
                {
                    for(var partIndex: number = 0; partIndex < innerResult.address_components.length; partIndex++)
                    {
                        var innerPart: google.maps.GeocoderAddressComponent = innerResult.address_components[partIndex];
                        var part: AddressPart = new AddressPart(innerPart.long_name, innerPart.short_name, innerPart.types.slice());
                        result.Parts.push(part);
                    }
                }

                results.push(result);
            }
        }

        return results;
    }

    private ConvertLocationType(locationType: google.maps.GeocoderLocationType): CoordinateType
    {
        switch(locationType)
        {
            case(google.maps.GeocoderLocationType.APPROXIMATE):
                return CoordinateType.Approximate;
            case (google.maps.GeocoderLocationType.GEOMETRIC_CENTER):
                return CoordinateType.Center;
            case (google.maps.GeocoderLocationType.RANGE_INTERPOLATED):
                return CoordinateType.Interpolated;
            case (google.maps.GeocoderLocationType.ROOFTOP):
                return CoordinateType.Rooftop;
            default:
                return CoordinateType.None;
        }
    }
}

export class GeocoderResult
{
    public Parts: Array<AddressPart>;
    public Address: string;
    public Geometry: Geometry;
    public IsPartial: boolean;
    public Id: string;
    public Localities: Array<string>;
    public Types: Array<string>;
}

export class AddressPart
{
    constructor(longName: string, shortName: string, types: string[])
    {
        this.LongName = longName;
        this.ShortName = shortName;
        this.Types = types;
    }

    public LongName: string;
    public ShortName: string;
    public Types: Array<string>;
}

export class Geometry
{
    public Bounds: Bounds;
    public Coordinates: Coordinates;
    public Viewport: Bounds;
}

export class Bounds
{
    constructor(east: number, north: number, south: number, west: number)
    {
        this.East = east;
        this.North = north;
        this.South = south;
        this.West = west;
    }

    public East: number;
    public North: number;
    public South: number;
    public West: number;
}

export class Coordinates
{
    constructor(latitude: number, longitude: number, type: CoordinateType)
    {
        this.Latitude = latitude;
        this.Longitude = longitude;
        this.Type = type;
    }

    public Latitude: number;
    public Longitude: number;
    public Type: CoordinateType;
}

export enum CoordinateType
{
    None = 0,
    Approximate = 1,
    Center = 2,
    Interpolated = 3,
    Rooftop = 4
}

export enum GeocoderStatus
{
    NoResults = 0,
    Error = 1,
    InvalidRequest = 2,
    InProgress = 3,
    Success = 4,
    LicensingIssue = 5,
    RequestDenied = 6
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/shared/geo/ovs.geocoding.ts